import {AsyncDataLoading} from '@frontend/components/AsyncDataLoading'
import type {
    AdPreviewDataForGrid,
    AdPreviewDataForList,
    AdsListGridColumnsConfig,
    AdsListPresentation,
    FilteredItemsListPageHeaderInfo,
    FilteredItemsPageUrlQueryParams,
    ItemsListRouteParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {AdsFromOtherLocations} from '@frontend/modules/public/ads/components/list/AdsFromOtherLocations'
import {AdsList} from '@frontend/modules/public/ads/components/list/AdsList'
import {useFilteredItemsLoader} from '@frontend/modules/public/ads/hooks/useFilteredItemsLoader'
import {
    useCallback,
    useEffect,
} from 'react'
import {
    useLocation,
    useParams,
} from 'react-router-dom'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'
import type {UILayout} from 'swayok-react-mdb-ui-kit/types/Common'
import {extractIdFromUrlSlug} from '../../../../helpers/extractIdFromUrlSlug'

interface FilteredAdsListProps {
    // Количество объявлений на странице:
    // 20 и 40 делится на 4 и на 5, что удобно для таблиц с 2, 4 или 5 колонками.
    pageLimit?: number
    adsPresentation?: AdsListPresentation
    setPageHeaderInfo: (header: FilteredItemsListPageHeaderInfo) => void
}

// Список отфильтрованных объявлений или исполнителей услуг.
export function FilteredAdsList(props: FilteredAdsListProps) {

    const {
        adsPresentation = 'grid',
        pageLimit = adsPresentation === 'list' ? 10 : 20,
        setPageHeaderInfo,
    } = props

    // Данные о текущем URL.
    const location = useLocation()
    const routeParams = useParams<ItemsListRouteParams>()
    const {
        urlQueryParams,
        urlQueryData,
    } = useUrlQueryParams<FilteredItemsPageUrlQueryParams>()

    const categoryId: number | null = extractIdFromUrlSlug(routeParams.slugWithId)

    const {
        loadItems,
        isLoading,
        items,
        totalItemsCount,
        loadingError,
        isAllItemsLoaded,
        onItemDataChange,
    } = useFilteredItemsLoader<AdPreviewDataForGrid | AdPreviewDataForList>({
        type: 'ads',
        listIdForAnalytics: 'search',
        adsPresentation,
        pageLimit,
        categoryId,
        filters: urlQueryData,
        setHeaderInfo: setPageHeaderInfo,
    })

    const gridColumns: AdsListGridColumnsConfig | number = adsPresentation === 'list'
        ? 1
        : {
            xxs: 2,
            xs: 2,
            sm: 2,
            md: 2,
            lg: 3,
            xl: 4,
            xxl: 4,
        }
    const sideMenuVisibleFromBsBreakpoint: UILayout['bootstrapSize'] = 'md'

    const onLoadMore = useCallback(
        (infiniteScroll?: boolean) => loadItems('next', false, infiniteScroll),
        [loadItems]
    )

    // Загрузка объявлений/исполнителей у при изменении URL (включая монтирование).
    useEffect(
        () => loadItems('filter'),
        [location.key, adsPresentation]
    )

    const hasLocationInUrlQuery: boolean = (
        parseInt(urlQueryParams.get('location') ?? '0') > 0
    )

    const shouldLoadAdsFromAnotherLocations: boolean = (
        isAllItemsLoaded
        && totalItemsCount !== null
        && totalItemsCount < pageLimit
        && hasLocationInUrlQuery
    )

    return (
        <AsyncDataLoading
            loading={isLoading !== null && isLoading !== 'next'}
            className="flex-1"
            error={loadingError}
            onReload={() => loadItems('filter')}
            render={() => (
                <div>
                    <AdsList
                        listUid="search_results"
                        ads={items}
                        isAllAdsLoaded={isAllItemsLoaded}
                        isLoadingMoreItems={isLoading === 'next'}
                        onLoadMore={onLoadMore}
                        gridColumns={gridColumns}
                        sideMenuVisibleFromBsBreakpoint={sideMenuVisibleFromBsBreakpoint}
                        allowPromoBanners
                        presentation={adsPresentation}
                        onAdDataChange={onItemDataChange}
                        infiniteScroll
                        disabled={isLoading === 'next'}
                    />

                    {shouldLoadAdsFromAnotherLocations && (
                        <AdsFromOtherLocations
                            categoryId={categoryId}
                            filters={urlQueryData}
                            adsPresentation={adsPresentation}
                            gridColumns={gridColumns}
                            sideMenuVisibleFromBsBreakpoint={sideMenuVisibleFromBsBreakpoint}
                        />
                    )}
                </div>
            )}
        />
    )
}
